import classNames from 'classnames';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Tag from '@/components/molecules/tag/tag';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { capitalizeFirstLetter } from '@/libraries/formatters/capitalize-first-letter';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import styles from './room-matrix-date.module.scss';

interface RoomMatrixDateProp {
  /** Class name to add */
  className?: string;
  /** The date to show */
  date: string;
  /** Indicates whether the date has a charter */
  isCharter?: boolean;
  /** Whether to display the loading state */
  isLoading?: boolean;
  /** The week number to show */
  weekNumber: number | null;
}

const RoomMatrixDate = ({
  className,
  date,
  isCharter = false,
  isLoading = false,
  weekNumber,
}: RoomMatrixDateProp) => {
  const dateFormatter = useDateFormatter([weekOfYear]);
  const { t } = useI18n();

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, styles.isLoading, className)}
      >
        <SkeletonLoader className={styles.loader} height="24px" width="64px" />

        <SkeletonLoader className={styles.loader} height="16px" width="96px" />
      </div>
    );
  }

  const formattedDate = dateFormatter.dateFormatter(date);

  const dayLabel = capitalizeFirstLetter(formattedDate.format('dddd'));
  const weekLabel =
    weekNumber !== null
      ? `, ${t('items.common.words.week')} ${weekNumber}`
      : '';

  return (
    <div className={classNames(styles.component, className)}>
      {isCharter && (
        <Tag
          className={styles.tag}
          text={t('items.common.words.charter')}
          variant="tertiary"
        />
      )}

      <div className={styles.text}>
        <Typography tagStyle="date">
          {formattedDate.format('DD MMM')}
        </Typography>

        <Typography tagStyle="bodySmall">
          {`${dayLabel}${weekLabel}`}
        </Typography>
      </div>
    </div>
  );
};

RoomMatrixDate.displayName = 'RoomMatrixDate';

export default RoomMatrixDate;
